export interface PluginRuntimeDescriptor {
  slug: string;
  name: string;
  version: string;
  entry: string;
  style: string | null;
}

export interface PluginActionContext {
  item?: unknown;
  auth: {
    isLoggedIn: boolean;
    isSubscribed: boolean;
  };
  settings: Record<string, unknown>;
  baseUrl: string;
  isOffline: boolean;
}

export interface PluginActionContribution {
  id: string;
  plugin: string;
  slot: string;
  label: string;
  icon?: 'download';
  order?: number;
  requiredPermission?: string;
  enabledSetting?: string;
  requiresOnline?: boolean;
  supports?: (context: PluginActionContext) => boolean;
  execute: (context: PluginActionContext) => void | Promise<void>;
}

export interface PluginPageContribution {
  id: string;
  plugin: string;
  path: string;
  title: string;
  requiredPermission?: string;
  mount: (
    element: HTMLElement,
    context: {
      baseUrl: string;
      path: string;
      dateRange?: {start: string; end: string};
    },
  ) => void | (() => void);
}

export interface PluginNavigationContribution {
  id: string;
  plugin: string;
  slot: string;
  label: string;
  path: string;
  pageId?: string;
  icon?: 'download' | 'notifications';
  order?: number;
  requiredPermission?: string;
}

export interface PluginAccountSettingsContribution {
  id: string;
  plugin: string;
  panelId: string;
  title: string;
  order?: number;
  requiredPermission?: string;
  requiresSubscription?: boolean;
  mount: (
    element: HTMLElement,
    context: {baseUrl: string; path: string},
  ) => void | (() => void);
}

interface PluginRuntimeApi {
  registerAction: (action: PluginActionContribution) => void;
  registerPage: (page: PluginPageContribution) => void;
  registerNavigation: (navigation: PluginNavigationContribution) => void;
  registerAccountSettings: (
    contribution: PluginAccountSettingsContribution,
  ) => void;
}

declare global {
  interface Window {
    IK1DesignPlugins?: PluginRuntimeApi;
  }
}

const actions = new Map<string, PluginActionContribution>();
const pages = new Map<string, PluginPageContribution>();
const navigation = new Map<string, PluginNavigationContribution>();
const accountSettings = new Map<string, PluginAccountSettingsContribution>();
let runtimePlugins: PluginRuntimeDescriptor[] = [];
let runtimeLoaded = false;

function changed() {
  window.dispatchEvent(new CustomEvent('ik1design:plugins-changed'));
}

function validIdentifier(value: unknown): value is string {
  return (
    typeof value === 'string' && /^[a-z0-9][a-z0-9._:-]{0,159}$/i.test(value)
  );
}

window.IK1DesignPlugins = {
  registerAction(action) {
    if (
      !validIdentifier(action?.id) ||
      !validIdentifier(action?.plugin) ||
      !validIdentifier(action?.slot) ||
      typeof action?.label !== 'string' ||
      typeof action?.execute !== 'function'
    ) {
      throw new Error('Invalid plugin action contribution.');
    }
    actions.set(action.id, action);
    changed();
  },
  registerPage(page) {
    const publicBasePath = `/plugins/${page?.plugin}`;
    const adminBasePath = `/admin/plugins/${page?.plugin}`;
    const isPublicPluginPath =
      typeof page?.path === 'string' &&
      (page.path === publicBasePath ||
        page.path.startsWith(`${publicBasePath}/`));
    const isAdminPluginPath =
      typeof page?.path === 'string' &&
      (page.path === adminBasePath ||
        page.path.startsWith(`${adminBasePath}/`));
    if (
      !validIdentifier(page?.id) ||
      !validIdentifier(page?.plugin) ||
      (!isPublicPluginPath && !isAdminPluginPath) ||
      typeof page?.title !== 'string' ||
      typeof page?.mount !== 'function'
    ) {
      throw new Error('Invalid plugin page contribution.');
    }
    pages.set(page.id, page);
    changed();
  },
  registerNavigation(item) {
    const publicBasePath = `/plugins/${item?.plugin}`;
    const adminBasePath = `/admin/plugins/${item?.plugin}`;
    const isPluginPath =
      typeof item?.path === 'string' &&
      (item.path === publicBasePath ||
        item.path.startsWith(`${publicBasePath}/`));
    const isAdminPluginPath =
      item?.slot?.startsWith('admin.') &&
      typeof item?.path === 'string' &&
      (item.path === adminBasePath ||
        item.path.startsWith(`${adminBasePath}/`));
    const isAdminReportPath =
      item?.slot === 'admin.reports.tabs' &&
      typeof item?.path === 'string' &&
      /^\/admin\/reports\/[a-z0-9-]+$/i.test(item.path) &&
      validIdentifier(item?.pageId);
    if (
      !validIdentifier(item?.id) ||
      !validIdentifier(item?.plugin) ||
      !validIdentifier(item?.slot) ||
      typeof item?.label !== 'string' ||
      !item.label.trim() ||
      item.label.length > 100 ||
      (!isPluginPath && !isAdminPluginPath && !isAdminReportPath)
    ) {
      throw new Error('Invalid plugin navigation contribution.');
    }
    navigation.set(item.id, item);
    changed();
  },
  registerAccountSettings(contribution) {
    if (
      !validIdentifier(contribution?.id) ||
      !validIdentifier(contribution?.plugin) ||
      typeof contribution?.panelId !== 'string' ||
      !/^[a-z][a-z0-9-]{0,99}$/i.test(contribution.panelId) ||
      typeof contribution?.title !== 'string' ||
      !contribution.title.trim() ||
      contribution.title.length > 100 ||
      typeof contribution?.mount !== 'function'
    ) {
      throw new Error('Invalid plugin account settings contribution.');
    }
    accountSettings.set(contribution.id, contribution);
    changed();
  },
};

export function getPluginActions(slot: string): PluginActionContribution[] {
  const active = new Set(runtimePlugins.map(plugin => plugin.slug));
  return [...actions.values()]
    .filter(action => action.slot === slot && active.has(action.plugin))
    .sort((left, right) => (left.order || 0) - (right.order || 0));
}

export function getPluginPage(
  path: string,
): PluginPageContribution | undefined {
  const active = new Set(runtimePlugins.map(plugin => plugin.slug));
  return [...pages.values()].find(
    page => page.path === path && active.has(page.plugin),
  );
}

export function getPluginPageById(
  id: string,
): PluginPageContribution | undefined {
  const active = new Set(runtimePlugins.map(plugin => plugin.slug));
  const page = pages.get(id);
  return page && active.has(page.plugin) ? page : undefined;
}

export function getPluginNavigation(
  slot: string,
): PluginNavigationContribution[] {
  const active = new Set(runtimePlugins.map(plugin => plugin.slug));
  return [...navigation.values()]
    .filter(item => item.slot === slot && active.has(item.plugin))
    .sort((left, right) => (left.order || 0) - (right.order || 0));
}

export function getPluginAccountSettings(): PluginAccountSettingsContribution[] {
  const active = new Set(runtimePlugins.map(plugin => plugin.slug));
  return [...accountSettings.values()]
    .filter(contribution => active.has(contribution.plugin))
    .sort((left, right) => (left.order || 0) - (right.order || 0));
}

export function getRuntimePlugin(
  slug: string,
): PluginRuntimeDescriptor | undefined {
  return runtimePlugins.find(plugin => plugin.slug === slug);
}

export function setRuntimePlugins(plugins: PluginRuntimeDescriptor[]) {
  runtimePlugins = plugins;
  runtimeLoaded = true;
  changed();
}

export function isPluginRuntimeLoaded(): boolean {
  return runtimeLoaded;
}
